// HTTP Client
import axios from "axios";

// Supabase Utilities
import { getSupabaseJwt, refreshSupabaseJwt } from "@/utils/supabase/token-manager";

// Notifications
import toast from "react-hot-toast";

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
});

// Flag to prevent multiple refresh calls at once
let isRefreshing = false;
let refreshPromise :any = null;
let failedQueue:any[] = [];

const processQueue = async (error: any, token?: string ) => {
  for (const { callback } of failedQueue) {
    await callback(error, token);
  }

  failedQueue = [];
};

// Request interceptor to add JWT token
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const token = await getSupabaseJwt();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error(`Error fetching Supabase JWT:, ${error}`);
    }
    return config;
  },
  (error) => {
    throw error;
  }
);

// Response interceptor to handle token expiry
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403 || error.response?.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          const token = await refreshPromise;
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return await axiosInstance(originalRequest);
          }
        }

        isRefreshing = true;

        try {
          refreshPromise = refreshSupabaseJwt();
          const newToken = await refreshPromise;
          axiosInstance.defaults.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          await processQueue(null, newToken);
          return await axiosInstance(originalRequest);
        } catch (err) {
          await processQueue(err, undefined);
          throw err;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      }
    }

    throw error;
  }
);

export default axiosInstance;
